// Pure, client-side medical calculators. Educational only — no data leaves the browser.
// Calculators return STABLE KEYS for categories so the UI can localize them (see toolsI18n.js).
// Internal math uses US units (mg/dL, kg, cm).

import {
  Droplet, Activity, Scale, HeartPulse, Waves, FlaskConical, ShieldAlert, TestTube, Sigma,
  Gauge, Baby, Pill, Ruler, Thermometer, PersonStanding,
} from "lucide-react";

// --- Unit conversions ---
export const conv = {
  glucoseToMmol: (mgdl) => mgdl / 18.0182,
  glucoseToMgdl: (mmol) => mmol * 18.0182,
  cholToMmol: (mgdl) => mgdl / 38.67,
  cholToMgdl: (mmol) => mmol * 38.67,
  tgToMmol: (mgdl) => mgdl / 88.57,
  tgToMgdl: (mmol) => mmol * 88.57,
  creatToUmol: (mgdl) => mgdl * 88.42,
  creatToMgdl: (umol) => umol / 88.42,
  lbToKg: (lb) => lb / 2.20462,
  kgToLb: (kg) => kg * 2.20462,
  inToCm: (inch) => inch * 2.54,
};

const r1 = (n) => Math.round(n * 10) / 10;
const r0 = (n) => Math.round(n);

// 1. A1C -> estimated average glucose (eAG)
export function a1cToEag(a1c) {
  const mgdl = 28.7 * a1c - 46.7;
  return { mgdl: r0(mgdl), mmol: r1(mgdl / 18.0182) };
}

// 2. Average glucose -> approximate A1C
export function glucoseToA1c(avgMgdl) {
  return r1((avgMgdl + 46.7) / 28.7);
}

// 3. BMI (kg, cm) with WHO category key
export function bmi(weightKg, heightCm) {
  const m = heightCm / 100;
  const value = weightKg / (m * m);
  let key, tone;
  if (value < 18.5) { key = "underweight"; tone = "amber"; }
  else if (value < 25) { key = "normal"; tone = "green"; }
  else if (value < 30) { key = "overweight"; tone = "amber"; }
  else if (value < 35) { key = "obese1"; tone = "red"; }
  else if (value < 40) { key = "obese2"; tone = "red"; }
  else { key = "obese3"; tone = "red"; }
  return { value: r1(value), key, tone };
}

// 4. Blood pressure category (ACC/AHA 2017) -> key
export function bpCategory(sys, dia) {
  if (sys > 180 || dia > 120) return { key: "crisis", tone: "red" };
  if (sys >= 140 || dia >= 90) return { key: "stage2", tone: "red" };
  if (sys >= 130 || dia >= 80) return { key: "stage1", tone: "amber" };
  if (sys >= 120 && dia < 80) return { key: "elevated", tone: "amber" };
  return { key: "normal", tone: "green" };
}

// 5. eGFR — CKD-EPI 2021 creatinine (race-free). scr mg/dL.
export function egfrCKDEPI(scr, age, sex) {
  const female = sex === "female";
  const k = female ? 0.7 : 0.9;
  const a = female ? -0.241 : -0.302;
  const ratio = scr / k;
  let val = 142 * Math.pow(Math.min(ratio, 1), a) * Math.pow(Math.max(ratio, 1), -1.2)
    * Math.pow(0.9938, age);
  if (female) val *= 1.012;
  return { value: r0(val), stageKey: ckdStageKey(val) };
}

function ckdStageKey(egfr) {
  if (egfr >= 90) return { key: "g1", tone: "green" };
  if (egfr >= 60) return { key: "g2", tone: "green" };
  if (egfr >= 45) return { key: "g3a", tone: "amber" };
  if (egfr >= 30) return { key: "g3b", tone: "amber" };
  if (egfr >= 15) return { key: "g4", tone: "red" };
  return { key: "g5", tone: "red" };
}

// 6. Creatinine clearance — Cockcroft-Gault. weight kg, scr mg/dL.
export function cockcroftGault(age, weightKg, scr, sex) {
  let val = ((140 - age) * weightKg) / (72 * scr);
  if (sex === "female") val *= 0.85;
  return { value: r0(val) };
}

// 7. Cholesterol risk indicator (educational). tc, hdl in mg/dL.
export function cholesterolRisk(tc, hdl, factors = {}) {
  const ratio = r1(tc / hdl);
  let levelKey, tone;
  if (ratio < 3.5) { levelKey = "low"; tone = "green"; }
  else if (ratio <= 5) { levelKey = "moderate"; tone = "amber"; }
  else { levelKey = "high"; tone = "red"; }
  const flags = ["smoker", "diabetes", "highBP", "familyHistory"].filter((k) => factors[k]);
  return { ratio, levelKey, tone, flags, factorCount: flags.length };
}

// 8. LDL — Friedewald (mg/dL). Invalid when TG >= 400.
export function ldlFriedewald(tc, hdl, tg) {
  if (tg >= 400) return { valid: false, value: null };
  return { valid: true, value: r0(tc - hdl - tg / 5) };
}

// 9. Non-HDL cholesterol (mg/dL).
export function nonHdl(tc, hdl) {
  return { value: r0(tc - hdl) };
}

// 10. Target heart rate (age-predicted max = 220 - age; Karvonen if resting given)
export function targetHeartRate(age, resting) {
  const maxHR = 220 - age;
  const useKarvonen = Number.isFinite(resting) && resting > 0;
  const band = (loPct, hiPct) => {
    if (useKarvonen) {
      const reserve = maxHR - resting;
      return [r0(resting + reserve * loPct), r0(resting + reserve * hiPct)];
    }
    return [r0(maxHR * loPct), r0(maxHR * hiPct)];
  };
  return {
    maxHR,
    method: useKarvonen ? "karvonen" : "simple",
    moderate: band(0.5, 0.7),
    vigorous: band(0.7, 0.85),
  };
}

// 11. Pregnancy due date (Naegele: LMP + 280 days)
export function dueDate(lmpISO) {
  const lmp = new Date(lmpISO + "T00:00:00");
  if (isNaN(lmp.getTime())) return null;
  const edd = new Date(lmp.getTime() + 280 * 86400000);
  const today = new Date();
  const days = Math.floor((today - lmp) / 86400000);
  const future = edd > today;
  return {
    edd: edd.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }),
    gaWeeks: days >= 0 ? Math.floor(days / 7) : null,
    gaDays: days >= 0 ? days % 7 : null,
    daysToGo: future ? Math.round((edd - today) / 86400000) : 0,
  };
}

// 12. Weight-based dose (mg = weight kg × mg/kg, optional single-dose cap)
export function weightBasedDose(weightKg, mgPerKg, maxMg) {
  let dose = weightKg * mgPerKg;
  let capped = false;
  if (Number.isFinite(maxMg) && maxMg > 0 && dose > maxMg) {
    dose = maxMg;
    capped = true;
  }
  return { dose: Math.round(dose * 10) / 10, capped };
}

// 13. Ideal body weight (Devine). heightCm, sex.
export function idealBodyWeight(heightCm, sex) {
  const inches = heightCm / 2.54;
  const over60 = Math.max(0, inches - 60);
  const base = sex === "male" ? 50 : 45.5;
  const kg = base + 2.3 * over60;
  return { kg: r1(kg), lb: r0(conv.kgToLb(kg)) };
}

// 14. Waist-to-height ratio. Both in cm.
export function waistToHeight(waistCm, heightCm) {
  const ratio = waistCm / heightCm;
  let key, tone;
  if (ratio < 0.5) { key = "healthy"; tone = "green"; }
  else if (ratio < 0.6) { key = "increased"; tone = "amber"; }
  else { key = "high"; tone = "red"; }
  return { ratio: Math.round(ratio * 100) / 100, key, tone };
}

// 15. Pediatric fever dose. weightKg, drug in {acetaminophen, ibuprofen}.
export function pediatricFever(weightKg, drug) {
  const spec = drug === "ibuprofen"
    ? { mgPerKg: 10, cap: 400, interval: "ibuInterval", warnKey: "ibuWarn" }
    : { mgPerKg: 15, cap: 1000, interval: "acetInterval", warnKey: "acetWarn" };
  const raw = weightKg * spec.mgPerKg;
  const perDose = Math.round(Math.min(raw, spec.cap));
  return { perDose, capped: raw > spec.cap, interval: spec.interval, warnKey: spec.warnKey, drug };
}

// Common OTC pediatric liquid concentrations (mg per mL of product).
export const PED_CONC = {
  acetaminophen: [
    { id: "acet160", mg: 160, ml: 5, typeKey: "concChild" }, // Children's suspension 160 mg/5 mL
  ],
  ibuprofen: [
    { id: "ibu100", mg: 100, ml: 5, typeKey: "concChild" },   // Children's suspension 100 mg/5 mL
    { id: "ibu50", mg: 50, ml: 1.25, typeKey: "concInfant" }, // Infant drops 50 mg/1.25 mL
  ],
};

// Convert a mg dose into mL for a given liquid concentration (rounded to 0.1 mL).
export function doseToMl(doseMg, conc) {
  const ml = (doseMg / conc.mg) * conc.ml;
  return Math.round(ml * 10) / 10;
}

// --- Tool registry (order = display order). Titles/desc localized in toolsI18n.js. ---
export const TOOLS = [
  { slug: "a1c-to-glucose", icon: Droplet, color: "rose" },
  { slug: "glucose-to-a1c", icon: Activity, color: "rose" },
  { slug: "bmi", icon: Scale, color: "teal" },
  { slug: "blood-pressure", icon: HeartPulse, color: "red" },
  { slug: "egfr", icon: Waves, color: "sky" },
  { slug: "creatinine-clearance", icon: FlaskConical, color: "sky" },
  { slug: "cholesterol-risk", icon: ShieldAlert, color: "violet" },
  { slug: "ldl", icon: TestTube, color: "violet" },
  { slug: "non-hdl", icon: Sigma, color: "violet" },
  { slug: "target-heart-rate", icon: Gauge, color: "teal" },
  { slug: "due-date", icon: Baby, color: "rose" },
  { slug: "medication-dose", icon: Pill, color: "sky" },
  { slug: "ideal-body-weight", icon: PersonStanding, color: "teal" },
  { slug: "waist-height-ratio", icon: Ruler, color: "teal" },
  { slug: "pediatric-fever", icon: Thermometer, color: "rose" },
];

export const toolBySlug = (slug) => TOOLS.find((t) => t.slug === slug);

export const COLOR = {
  rose: { ring: "border-rose-200", chip: "bg-rose-100 text-rose-700", icon: "text-rose-600", grad: "from-rose-500 to-pink-600" },
  teal: { ring: "border-teal-200", chip: "bg-teal-100 text-teal-700", icon: "text-teal-600", grad: "from-teal-500 to-emerald-600" },
  red: { ring: "border-red-200", chip: "bg-red-100 text-red-700", icon: "text-red-600", grad: "from-red-500 to-rose-600" },
  sky: { ring: "border-sky-200", chip: "bg-sky-100 text-sky-700", icon: "text-sky-600", grad: "from-sky-500 to-blue-600" },
  violet: { ring: "border-violet-200", chip: "bg-violet-100 text-violet-700", icon: "text-violet-600", grad: "from-violet-500 to-purple-600" },
};

export const TONE = {
  green: "bg-green-50 border-green-200 text-green-800",
  amber: "bg-amber-50 border-amber-200 text-amber-800",
  red: "bg-red-50 border-red-200 text-red-800",
  neutral: "bg-slate-50 border-slate-200 text-slate-800",
};
